import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Building2, Package, ShoppingCart, Sparkles, ArrowLeft, ArrowRight, X } from "lucide-react";

/** First-run onboarding tour. Shows once, dismissable, records completion in localStorage. */
const STEPS = [
  {
    icon: Building2,
    title: "👋 مرحباً بك في محاسبتي!",
    body: "دعنا نأخذك في جولة قصيرة (30 ثانية) لتتعرف على أهم شيء. يمكنك تخطيها في أي وقت.",
  },
  {
    icon: Building2,
    title: "١. اختر أو أنشئ شركة",
    body: "من الشريط العلوي اختر الشركة النشطة. إذا كنت جديداً، اضغط «الشركات» في السايدبار وأنشئ شركتك الأولى.",
    cta: { label: "شركاتي", to: "/companies" },
  },
  {
    icon: Package,
    title: "٢. أضف أصنافك أو جرّب بيانات جاهزة",
    body: "من «الأصناف» يمكنك إضافة صنف يدوياً، مسحه بالباركود، أو استيراد Excel. لبدء سريع، جرّب «تعبئة تجريبية» في لوحة التحكم — 20 صنف + عملاء + فواتير جاهزة.",
    cta: { label: "الأصناف", to: "/products" },
  },
  {
    icon: ShoppingCart,
    title: "٣. ابدأ البيع الفوري",
    body: "من «نقطة البيع» امسح الباركود بالماسح أو كاميرا الهاتف — الفاتورة تُطبع تلقائياً على طابعة 80mm. سرعة كاملة.",
    cta: { label: "نقطة البيع", to: "/pos" },
  },
  {
    icon: Sparkles,
    title: "٤. اطلب من المساعد الذكي أي شيء",
    body: "قل له بالعربية: «أضف صنف كوكاكولا سعره 500»، «سجل مصروف كهرباء 25000»، «كم مبيعاتي اليوم؟» وسينفّذها فوراً. تجربتك ستتغير!",
    cta: { label: "جرّب المساعد", to: "/ai" },
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Keep the demo immediately interactive; the ordinary onboarding remains unchanged in production.
    if (process.env.REACT_APP_MOCK_MODE === "true") { localStorage.setItem("onboarding_done", "1"); return; }
    const done = localStorage.getItem("onboarding_done");
    if (!done) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const finish = () => {
    localStorage.setItem("onboarding_done", "1");
    setOpen(false);
  };

  const s = STEPS[step];
  if (!s) return null;
  const Icon = s.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && finish()}>
      <DialogContent className="max-w-md" data-testid="onboarding-tour">
        <VisuallyHidden.Root>
          <DialogTitle>جولة البدء السريع</DialogTitle>
          <DialogDescription>خطوات تعريفية للاستخدام الأول</DialogDescription>
        </VisuallyHidden.Root>
        <button onClick={finish} className="absolute top-3 end-3 text-muted-foreground hover:text-foreground" data-testid="onboarding-close" aria-label="إغلاق">
          <X className="w-4 h-4" />
        </button>
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center mb-4">
            <Icon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-heading font-bold mb-2">{s.title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed px-2">{s.body}</p>
        </div>
        <div className="flex items-center justify-center gap-1 py-2">
          {STEPS.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-primary" : "w-2 bg-border"}`} />
          ))}
        </div>
        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button variant="ghost" onClick={finish} data-testid="onboarding-skip">تخطي</Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onboarding-back">
                <ArrowRight className="w-4 h-4 me-1" /> رجوع
              </Button>
            )}
            {s.cta && (
              <Button asChild variant="outline" onClick={finish} data-testid="onboarding-cta">
                <Link to={s.cta.to}>{s.cta.label}</Link>
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} data-testid="onboarding-next">
                التالي <ArrowLeft className="w-4 h-4 ms-1" />
              </Button>
            ) : (
              <Button onClick={finish} data-testid="onboarding-finish">ابدأ الآن</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
